import { Toaster } from "@/components/ui/sonner";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";

import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Services from "@/components/Services";
import Login from "@/pages/Login";

// Dashboard layouts (no header/footer)
import ResetPasswordPage from "@/pages/ResetPasswordPage.tsx";
import SuperAdminLayout from "@/components/layout/SuperAdminLayout";
import SuperAdminDashboard from "@/features/dashboard/pages/Dashboard";
import DashboardOrderDetails from "@/features/dashboard/pages/DashboardOrderDetails";

// Feature-based imports
import VendorList from "@/features/vendors/pages/VendorList";
import VendorDetails from "@/features/vendors/pages/VendorDetails";

import ClinicList from "@/features/clinics/pages/ClinicList";

import ClientsList from "@/features/clients/pages/ClientList";
import ClientDetails from "@/features/clients/pages/ClientDetails";

import TestsList from "@/features/tests/pages/TestList";

import ReportsList from "@/features/reports/pages/ReportList";
import VisitorLabReportsList from "@/features/reports/pages/VisitorLabReportsList";

import DrugPanelList from "@/features/drug-panels/pages/DrugPanelList";

import LabsListPage from "@/features/labs/pages/LabsListPage";
import CreateLabPage from "@/features/labs/pages/CreateLabPage";
import MasterDataManagement from "@/features/master-data/pages/MasterDataManagement";

import ClientInvoiceReport from "@/features/reports-v2/pages/ClientInvoiceReport";
import PricingMatrix from "@/features/reports-v2/pages/PricingMatrix";
import LocationUtilization from "@/features/reports-v2/pages/LocationUtilization";
import ReportingLayout from "@/features/reports-v2/pages/ReportingLayout";

import LocationSearch from "@/pages/public/LocationSearch";

import SchedulingDemographics from "@/features/scheduling/pages/SchedulingDemographics";

// Visitor management imports
import VisitorsList from "@/pages/lab-admin/VisitorsList";
import VisitorTestsList from "@/pages/lab-admin/VisitorTestsList";
import VisitorOrdersPage from "@/pages/lab-admin/VisitorOrdersPage";
import ClientEmployeesPage from "@/pages/client/ClientEmployeesPage";

// Role-based redirector
const DashboardRedirect = () => {
  return <Navigate to="/login" replace />;
};

export default function AppRouter() {
  return (
    <>
      <Toaster />
      <BrowserRouter>
        <Routes>
          {/* PUBLIC ROUTES — show header + footer */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route
            path="/about"
            element={
              <>
                <Header />
                <About />
                <Footer />
              </>
            }
          />
          <Route
            path="/services"
            element={
              <>
                <Header />
                <Services />
                <Footer />
              </>
            }
          />
          <Route
            path="/contact"
            element={
              <>
                <Header />
                <Contact />
                <Footer />
              </>
            }
          />
          <Route
            path="/login"
            element={
              <>
                <Login />
                <Footer />
              </>
            }
          />
          <Route
            path="/reset-password/:token"
            element={
              <>
                <Header />
                <ResetPasswordPage />
                <Footer />
              </>
            }
          />
          <Route
            path="/search"
            element={
              <>
                <Header />
                <LocationSearch />
                <Footer />
              </>
            }
          />

          {/* Global Dashboard Entry Point */}
          <Route path="/dashboard" element={<DashboardRedirect />} />

          {/* SUPER ADMIN LAYOUT */}
          <Route path="/super-admin" element={<SuperAdminLayout />}>
            <Route path="dashboard" element={<SuperAdminDashboard />} />
            <Route path="dashboard/orders/:appointmentId" element={<DashboardOrderDetails />} />
            <Route path="vendors" element={<VendorList />} />
            <Route path="vendors/:vendorId" element={<VendorDetails />} />
            <Route path="clinics" element={<ClinicList />} />
            <Route path="clients" element={<ClientsList />} />
            <Route path="tests" element={<TestsList />} />
            <Route path="drug-panels" element={<DrugPanelList />} />
            <Route path="reports" element={<ReportsList />} />
            <Route path="reports-v2" element={<ReportingLayout />}>
              <Route index element={<Navigate to="revenue" replace />} />
              <Route path="revenue" element={<ClientInvoiceReport />} />
              <Route path="pricing-matrix" element={<PricingMatrix />} />
              <Route path="utilization" element={<LocationUtilization />} />
            </Route>
            <Route path="visitor-lab-reports" element={<VisitorLabReportsList />} />
            <Route path="visitors" element={<VisitorsList />} />
            <Route path="visitors/:visitorId/orders" element={<VisitorOrdersPage />} />
            <Route path="visitor-tests" element={<VisitorTestsList />} />
            <Route path="labs" element={<LabsListPage />} />
            <Route path="labs/create" element={<CreateLabPage />} />
            <Route path="master-data" element={<MasterDataManagement />} />
            <Route path="vendors/:vendorId/clinics" element={<VendorDetails />} />
            <Route path="scheduling" element={<SchedulingDemographics />} />
            <Route index element={<SuperAdminDashboard />} />
            <Route path="clients/:id" element={<ClientDetails />} />
          </Route>

          {/* CLIENT PORTAL LAYOUT */}
          <Route path="/client" element={<SuperAdminLayout />}>
            <Route path="dashboard" element={<SuperAdminDashboard />} />
            <Route path="dashboard/orders/:appointmentId" element={<DashboardOrderDetails />} />
            <Route path="employees" element={<ClientEmployeesPage />} />
            <Route path="visitors" element={<VisitorsList />} />
            <Route path="visitors/:visitorId/orders" element={<VisitorOrdersPage />} />
            <Route path="reports/invoices" element={<ClientInvoiceReport />} />
            <Route path="scheduling" element={<SchedulingDemographics />} />
            <Route index element={<SuperAdminDashboard />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </>
  );
}
